import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MdGroups, MdHowToReg, MdMedicalServices, MdTaskAlt,
  MdTv, MdCalendarViewWeek, MdLocalHospital,
} from 'react-icons/md';
import { PageShell, Badge, DataTable, StatCard } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHospitalDirectory } from '../../hooks/useHospitalDirectory';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { printOpReceipt, printOpToken, printAppointmentSlip } from '../../utils/hospitalPdf';
import { Q, Q_TAB, queueRowId, filterQueueByTab, todayStr } from '../../config/queueStatus';
import { mergeEmrSettings, serviceNamesFromSettings, servicePresetsFromSettings } from '../../config/emrSettings';
import { downloadQueueCsv } from '../../utils/exportCsv';
import WalkInWizard from './WalkInWizard';
import BookAppointmentForm, { QueueRowActions } from './QueueRowActions';

function mapQueue(row) {
  return {
    ...row,
    id: queueRowId(row),
    queueId: queueRowId(row),
    name: row.patientName || row.name,
  };
}

/* ── Waiting Room live display (DocPulse-style) ── */
function WaitingRoomDisplay({ queue, doctors }) {
  const byDoctor = doctors.reduce((acc, d) => {
    acc[d.name] = queue.filter((q) => q.doctor === d.name && q.status !== Q.COMPLETED);
    return acc;
  }, {});

  const statusColor = (s) => {
    if (s === Q.WAITING_DOCTOR) return '#10b981';
    if (s === Q.IN_CONSULTATION) return '#8b5cf6';
    if (s === Q.CHECKED_IN || s === Q.AWAITING_PAYMENT) return '#f59e0b';
    return '#94a3b8';
  };
  const statusLabel = (s) => {
    if (s === Q.WAITING_DOCTOR) return 'Waiting';
    if (s === Q.IN_CONSULTATION) return 'Consulting';
    if (s === Q.CHECKED_IN) return 'Checked-in';
    if (s === Q.AWAITING_PAYMENT) return 'Payment';
    return s;
  };

  return (
    <div className="wr-board">
      <div className="wr-header">
        <MdLocalHospital style={{ fontSize: 22 }} />
        <h2 className="wr-title">Live Waiting Room</h2>
        <span className="wr-live-badge"><span className="wr-live-dot" />LIVE</span>
      </div>
      {doctors.length === 0 && (
        <p style={{ color: '#64748b', padding: '24px' }}>No doctors configured yet.</p>
      )}
      <div className="wr-doctor-grid">
        {doctors.map((d) => {
          const pts = byDoctor[d.name] || [];
          const calling = pts.find((p) => p.status === Q.IN_CONSULTATION);
          return (
            <div key={d.staffId} className="wr-doctor-col">
              <div className="wr-doc-head">
                <span className="wr-doc-avatar">{(d.name || 'D').charAt(0)}</span>
                <div>
                  <div className="wr-doc-name">{d.name}</div>
                  <div className="wr-doc-dept">{d.department || d.specialty || 'General'}</div>
                </div>
              </div>
              {calling && (
                <div className="wr-calling">
                  <div className="wr-calling-label">Now calling</div>
                  <div className="wr-calling-token">Token #{calling.token || '—'}</div>
                  <div className="wr-calling-name">{calling.name}</div>
                </div>
              )}
              <div className="wr-queue-list">
                {pts.filter((p) => p.status !== Q.IN_CONSULTATION).map((p, i) => (
                  <div key={p.id || i} className="wr-queue-row">
                    <span className="wr-token-num">{p.token || `#${i + 1}`}</span>
                    <span className="wr-patient-name">{p.name}</span>
                    <span
                      className="wr-status-dot"
                      style={{ background: statusColor(p.status) }}
                      title={statusLabel(p.status)}
                    />
                  </div>
                ))}
                {pts.length === 0 && <div className="wr-empty">Queue clear</div>}
              </div>
              <div className="wr-count">{pts.length} waiting</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Weekly Schedule view (DocPulse-style) ── */
function WeeklySchedule({ queue, doctors }) {
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date();
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());
  const weekDates = DAYS.map((_, i) => {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    return d;
  });

  const getCount = (doctorName, dayIdx) => {
    const d = weekDates[dayIdx];
    return queue.filter((q) => {
      if (q.doctor !== doctorName) return false;
      const qDate = q.time ? new Date(q.time) : null;
      if (!qDate || Number.isNaN(qDate.getTime())) return false;
      return qDate.getDay() === d.getDay() && Math.abs(qDate - d) < 7 * 86400000;
    }).length;
  };

  return (
    <div className="ws-wrap">
      <div className="ws-table-wrap">
        <table className="ws-table">
          <thead>
            <tr>
              <th className="ws-th">Doctor</th>
              {weekDates.map((d, i) => (
                <th
                  key={i}
                  className={`ws-th${d.toDateString() === today.toDateString() ? ' ws-today' : ''}`}
                >
                  <div>{DAYS[i]}</div>
                  <div className="ws-date-num">{d.getDate()}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {doctors.map((doc) => (
              <tr key={doc.staffId}>
                <td className="ws-doctor-cell">
                  <span className="ws-doc-dot" />{doc.name}
                  {doc.department && <div className="hint">{doc.department}</div>}
                </td>
                {weekDates.map((d, di) => {
                  const cnt = getCount(doc.name, di);
                  return (
                    <td
                      key={di}
                      className={`ws-slot-cell${d.toDateString() === today.toDateString() ? ' ws-today-col' : ''}`}
                    >
                      {cnt > 0 ? (
                        <span className="ws-slot-badge">{cnt} pt</span>
                      ) : (
                        <span className="ws-slot-empty">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
            {doctors.length === 0 && (
              <tr>
                <td colSpan={8} style={{ color: '#94a3b8', padding: '20px', textAlign: 'center' }}>
                  No doctors configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OpQueue({ title = 'Q Page', subtitle = 'Reception — walk-in, booked, My OPD, and completed visits' }) {
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const { data, loading, error, reload } = useLiveData(() => ipApi.getQueue({ limit: 150 }), []);
  const { data: dir } = useHospitalDirectory();
  const { data: patientSearch } = useLiveData(() => ipApi.getPatients('', 200), []);
  const { data: emrData } = useLiveData(() => ipApi.getEmrSettings(), []);
  const emrSettings = mergeEmrSettings(emrData?.settings);
  const customSections = emrSettings.queueCustomSections || [];
  const doctors = dir?.doctors || [];
  const queue = (data?.queue || []).map(mapQueue);

  const [mainTab, setMainTab] = useState('qpage'); // 'qpage' | 'waiting-room' | 'weekly' | 'billing'
  const [qTab, setQTab] = useState(Q_TAB.MY_OPD);
  const [showWalkIn, setShowWalkIn] = useState(false);
  const [showBook, setShowBook] = useState(false);
  const [doctorFilter, setDoctorFilter] = useState('');
  const [busy, setBusy] = useState(null);
  const [actionError, setActionError] = useState('');

  const patchQueueRow = async (row, patch) => {
    const id = queueRowId(row);
    setBusy(id);
    setActionError('');
    try {
      await ipApi.patchQueue(id, patch);
      await reload();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const collectFee = async (row) => {
    const id = queueRowId(row);
    const fee = Number(row.fee || 0);
    if (!(fee > 0)) {
      setActionError('Set consultation fee before billing (edit queue row or walk-in fee).');
      return;
    }
    setBusy(id);
    setActionError('');
    try {
      const res = await ipApi.createOpReceipt({
        patientName: row.name || row.patientName,
        patientId: row.patientId,
        patientPhone: row.patientPhone,
        doctor: row.doctor,
        amount: fee,
        queueId: id,
        paymentMode: row.paymentMode || 'Cash',
        service: row.service || 'Consultation',
        hospitalId: user.hospitalId,
      });
      if (res?.receipt) printOpReceipt(res.receipt, hospital, user);
      await reload();
    } catch (e) {
      setActionError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const statusTone = (s) => {
    if (s === Q.COMPLETED) return 'success';
    if (s === Q.WAITING_DOCTOR || s === Q.IN_CONSULTATION) return 'success';
    if (s === Q.CANCELLED || s === Q.NO_SHOW || s === Q.MISSED) return 'danger';
    if (s === Q.AWAITING_PAYMENT || s === Q.CHECKED_IN) return 'warning';
    return 'info';
  };

  const today = todayStr();
  const tabCounts = Object.fromEntries(
    [...Object.values(Q_TAB), ...customSections.map((s) => s.id)].map((t) => [
      t,
      filterQueueByTab(queue, t, today, customSections).length,
    ]),
  );
  const doctorFiltered = doctorFilter ? queue.filter((q) => q.doctor === doctorFilter) : queue;
  const sectionRows = filterQueueByTab(doctorFiltered, qTab, today, customSections);

  const columns = [
    {
      key: 'name', label: 'Patient', render: (r) => (
        <div className="cell-stack">
          <span className="cell-strong">{r.name}</span>
          {r.age && <span className="cell-sub">{r.age}y / {r.gender || ''}{r.bloodGroup ? ` · ${r.bloodGroup}` : ''}</span>}
          {r.patientPhone && <span className="cell-sub">📞 {r.patientPhone}</span>}
        </div>
      ),
    },
    { key: 'patientId', label: 'UHID', render: (r) => r.patientId || <span style={{ color: '#94a3b8', fontSize: 11 }}>Walk-in</span> },
    {
      key: 'visitType', label: 'Visit', render: (r) => {
        const colors = { New: '#3b82f6', 'Follow-up': '#10b981', Emergency: '#ef4444', 'Walk-in': '#f59e0b' };
        const vt = r.visitType || 'New';
        return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: `${colors[vt]}20`, color: colors[vt], border: `1px solid ${colors[vt]}40` }}>{vt}</span>;
      },
    },
    { key: 'reason', label: 'Chief Complaint', render: (r) => r.reason || '—' },
    { key: 'doctor', label: 'Doctor' },
    {
      key: 'tags', label: 'Tags', render: (r) => (
        (r.tags || []).length ? (
          <span className="q-tag-inline">{(r.tags || []).slice(0, 2).join(', ')}{(r.tags || []).length > 2 ? '…' : ''}</span>
        ) : '—'
      ),
    },
    { key: 'token', label: 'Token', render: (r) => r.token ? <strong style={{ color: '#6366f1' }}>#{r.token}</strong> : '—' },
    { key: 'time', label: 'Time', render: (r) => r.time ? new Date(r.time).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—' },
    { key: 'status', label: 'Status', render: (r) => (
      <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <Badge tone={statusTone(r.status)}>{r.status}</Badge>
        {r.admittedToIpd && (
          <Link
            to={r.admissionId ? `/ipd/chart/${r.admissionId}` : '/ipd/list'}
            className="badge"
            style={{ background: '#dbeafe', color: '#1d4ed8', fontWeight: 700, textDecoration: 'none', fontSize: 10 }}
          >
            🏥 Admitted
          </Link>
        )}
      </span>
    ) },
    { key: 'fee', label: 'Fee', render: (r) => `₹${r.fee || 0}${r.paymentMode ? ` (${r.paymentMode})` : ''}` },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <QueueRowActions
          row={r}
          busy={busy === queueRowId(r)}
          hospital={hospital}
          user={user}
          onPatch={patchQueueRow}
          onCollectFee={collectFee}
          onReload={reload}
          onPrintToken={(row) => printOpToken(row, hospital, user)}
          onPrintAppt={(row) => printAppointmentSlip(row, hospital, user)}
        />
      ),
    },
  ];

  return (
    <PageShell
      title={title}
      subtitle={subtitle}
      actions={
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => downloadQueueCsv(doctorFiltered, today)}
            title="Download today's queue as CSV"
          >
            ⬇ CSV
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setMainTab('waiting-room')}
            title="Live waiting room display"
          >
            <MdTv /> Waiting Room
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              const token = localStorage.getItem('hcen_ip_token') || '';
              const qs = new URLSearchParams();
              if (user?.hospitalId) qs.set('hospitalId', user.hospitalId);
              if (token) qs.set('token', token);
              const suffix = qs.toString() ? `?${qs.toString()}` : '';
              window.open(`/display/waiting-room${suffix}`, '_blank', 'noopener');
            }}
            title="Open fullscreen TV display for waiting area"
          >
            🖥 TV Screen
          </button>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setMainTab('weekly')}
            title="Weekly schedule"
          >
            <MdCalendarViewWeek /> Schedule
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => { setQTab(Q_TAB.NEW); setShowWalkIn(true); setMainTab('qpage'); }}
          >
            + New walk-in
          </button>
        </div>
      }
    >
      {(error || actionError) && <div className="error mb">{error || actionError}</div>}

      {/* ── Main view tabs ── */}
      <div className="period-tabs card mb">
        <button type="button" className={`emr-chip ${mainTab === 'qpage' ? 'active' : ''}`} onClick={() => setMainTab('qpage')}>
          <MdGroups /> Q Page ({doctorFiltered.filter((q) => q.status !== Q.COMPLETED).length})
        </button>
        <button type="button" className={`emr-chip ${mainTab === 'waiting-room' ? 'active' : ''}`} onClick={() => setMainTab('waiting-room')}>
          <MdTv /> Waiting Room
        </button>
        <button type="button" className={`emr-chip ${mainTab === 'weekly' ? 'active' : ''}`} onClick={() => setMainTab('weekly')}>
          <MdCalendarViewWeek /> Weekly Schedule
        </button>
        <button type="button" className={`emr-chip ${mainTab === 'billing' ? 'active' : ''}`} onClick={() => setMainTab('billing')} style={{ background: mainTab === 'billing' ? '#10b981' : '', color: mainTab === 'billing' ? '#fff' : '#10b981', fontWeight: 800, border: '2px solid #10b981' }}>
          💳 Billing Counter
        </button>
      </div>

      {mainTab === 'waiting-room' && <WaitingRoomDisplay queue={queue} doctors={doctors} />}
      {mainTab === 'weekly' && <WeeklySchedule queue={queue} doctors={doctors} />}
      {mainTab === 'billing' && <ReceptionBillingTab queue={queue} hospital={hospital} user={user} onReceipt={reload} emrSettings={emrSettings} />}

      {mainTab === 'qpage' && (
        <>
          {/* Q Page workflow sections */}
          <div className="q-page-tabs card mb">
            <button type="button" className={`q-page-tab${qTab === Q_TAB.NEW ? ' active' : ''}`} onClick={() => { setQTab(Q_TAB.NEW); setShowWalkIn(true); setShowBook(false); }}>
              + New
            </button>
            <button type="button" className={`q-page-tab${qTab === Q_TAB.BOOKED ? ' active' : ''}`} onClick={() => { setQTab(Q_TAB.BOOKED); setShowWalkIn(false); setShowBook(false); }}>
              Booked <span className="q-tab-count">{tabCounts[Q_TAB.BOOKED]}</span>
            </button>
            <button type="button" className={`q-page-tab${qTab === Q_TAB.FOLLOWUPS ? ' active' : ''}`} onClick={() => { setQTab(Q_TAB.FOLLOWUPS); setShowWalkIn(false); setShowBook(false); }}>
              Follow-ups <span className="q-tab-count">{tabCounts[Q_TAB.FOLLOWUPS]}</span>
            </button>
            <button type="button" className={`q-page-tab${qTab === Q_TAB.OTHER ? ' active' : ''}`} onClick={() => { setQTab(Q_TAB.OTHER); setShowWalkIn(false); setShowBook(false); }}>
              Other <span className="q-tab-count">{tabCounts[Q_TAB.OTHER]}</span>
            </button>
            <button type="button" className={`q-page-tab${qTab === Q_TAB.MY_OPD ? ' active' : ''}`} onClick={() => { setQTab(Q_TAB.MY_OPD); setShowWalkIn(false); setShowBook(false); }}>
              My OPD <span className="q-tab-count">{tabCounts[Q_TAB.MY_OPD]}</span>
            </button>
            <button type="button" className={`q-page-tab${qTab === Q_TAB.COMPLETED ? ' active' : ''}`} onClick={() => { setQTab(Q_TAB.COMPLETED); setShowWalkIn(false); setShowBook(false); }}>
              Completed <span className="q-tab-count">{tabCounts[Q_TAB.COMPLETED]}</span>
            </button>
            {customSections.map((s) => (
              <button key={s.id} type="button" className={`q-page-tab${qTab === s.id ? ' active' : ''}`} onClick={() => { setQTab(s.id); setShowWalkIn(false); setShowBook(false); }}>
                {s.label} <span className="q-tab-count">{tabCounts[s.id] ?? 0}</span>
              </button>
            ))}
          </div>

          {qTab === Q_TAB.NEW && showWalkIn && (
            <WalkInWizard
              hospital={hospital}
              user={user}
              patients={patientSearch?.patients || []}
              emrSettings={emrSettings}
              onDone={() => { setShowWalkIn(false); setQTab(Q_TAB.MY_OPD); reload(); }}
              onCancel={() => { setShowWalkIn(false); setQTab(Q_TAB.MY_OPD); }}
            />
          )}

          {(qTab === Q_TAB.BOOKED || qTab === Q_TAB.FOLLOWUPS) && (
            <div style={{ marginBottom: 12 }}>
              {!showBook ? (
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowBook(true)}>
                  + Book {qTab === Q_TAB.FOLLOWUPS ? 'follow-up' : 'appointment'}
                </button>
              ) : (
                <BookAppointmentForm
                  user={user}
                  patients={patientSearch?.patients || []}
                  defaultVisitType={qTab === Q_TAB.FOLLOWUPS ? 'Follow-up' : 'New'}
                  onDone={() => { setShowBook(false); reload(); }}
                  onCancel={() => setShowBook(false)}
                />
              )}
            </div>
          )}

          <div className="stat-grid">
            <StatCard label="My OPD today" value={tabCounts[Q_TAB.MY_OPD]} accent="#4f8ef7" icon={MdGroups} loading={loading} />
            <StatCard label="Booked" value={tabCounts[Q_TAB.BOOKED] + tabCounts[Q_TAB.FOLLOWUPS]} accent="#f59e0b" icon={MdHowToReg} loading={loading} />
            <StatCard label="In consultation" value={doctorFiltered.filter((q) => q.status === Q.IN_CONSULTATION).length} accent="#8b5cf6" icon={MdMedicalServices} loading={loading} />
            <StatCard label="Completed today" value={tabCounts[Q_TAB.COMPLETED]} accent="#64748b" icon={MdTaskAlt} loading={loading} />
          </div>

          {doctors.length > 0 && (
            <div className="period-tabs card mb">
              <button type="button" className={`emr-chip ${!doctorFilter ? 'active' : ''}`} onClick={() => setDoctorFilter('')}>All doctors</button>
              {doctors.map((d) => (
                <button key={d.staffId} type="button" className={`emr-chip ${doctorFilter === d.name ? 'active' : ''}`} onClick={() => setDoctorFilter(d.name)}>
                  {d.name}
                </button>
              ))}
            </div>
          )}
          {qTab !== Q_TAB.NEW && (
            <DataTable
              columns={columns}
              rows={sectionRows}
              keyField="id"
              loading={loading}
              emptyMessage={
                qTab === Q_TAB.MY_OPD
                  ? 'No patients in My OPD — use + New for walk-in or check-in from Booked.'
                  : 'No records in this section.'
              }
            />
          )}
        </>
      )}
    </PageShell>
  );
}

/* Reception Billing Counter Tab */
function ReceptionBillingTab({ queue, hospital, user, onReceipt, emrSettings = {} }) {
  const BILLING_EXTRAS = ['IP Advance', 'IP Part Payment', 'Certificate / Report Charges', 'Ambulance', 'Other'];
  const SERVICES = serviceNamesFromSettings(emrSettings, BILLING_EXTRAS);
  const PRESETS = servicePresetsFromSettings(emrSettings);
  const defaultService = PRESETS[0] || { label: 'OPD Consultation', amount: 500 };
  const PAYMENT_MODES = ['Cash','UPI / GPay','Debit Card','Credit Card','NEFT / Bank Transfer','Insurance / TPA','Free / Waived'];
  const inrFmt = (n) => `\u20b9${Number(n||0).toLocaleString('en-IN')}`;

  const [form, setForm] = React.useState({
    patientName: '', patientId: '', patientPhone: '', doctor: '', queueId: '',
    paymentMode: 'Cash', txnRef: '', notes: '',
    lines: [{ service: defaultService.label, desc: '', amount: defaultService.amount }],
  });
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState('');
  const [receipts, setReceipts] = React.useState([]);
  const lineTotal = form.lines.reduce((s, l) => s + Number(l.amount || 0), 0);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await ipApi.getReceipts(80);
        const today = new Date().toISOString().slice(0, 10);
        const todayRows = (data.receipts || []).filter((r) => (r.createdAt || '').slice(0, 10) === today);
        if (!cancelled) setReceipts(todayRows);
      } catch { /* keep empty */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { service: SERVICES[1] || SERVICES[0] || 'Other', desc: '', amount: '' }] }));
  const removeLine = (i) => setForm(f => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));
  const updateLine = (i, patch) => setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...patch } : l) }));

  function pickFromQueue(queueId) {
    const row = queue.find(q => q.id === queueId || q.queueId === queueId);
    if (!row) return;
    setForm(f => ({
      ...f,
      queueId: queueRowId(row),
      patientName: row.name || row.patientName || '',
      patientId: row.patientId || '',
      patientPhone: row.patientPhone || '',
      doctor: row.doctor || '',
    }));
  }

  const createReceipt = async (e) => {
    e.preventDefault();
    if (!form.patientName.trim()) { setMsg('Patient name required.'); return; }
    if (lineTotal <= 0) { setMsg('Add at least one charge.'); return; }
    setBusy(true); setMsg('');
    try {
      const services = form.lines.filter((l) => Number(l.amount) > 0).map((l) => l.service).join(', ');
      const res = await ipApi.createOpReceipt({
        patientName: form.patientName.trim(),
        patientId: form.patientId || undefined,
        patientPhone: form.patientPhone || undefined,
        doctor: form.doctor || undefined,
        amount: lineTotal,
        paymentMode: form.paymentMode,
        txnRef: form.txnRef || undefined,
        service: services || 'OPD Services',
        desc: form.lines.map((l) => `${l.service}${l.desc ? `: ${l.desc}` : ''} ₹${l.amount}`).join('; '),
        notes: form.notes,
        queueId: form.queueId || undefined,
        hospitalId: user?.hospitalId,
      });
      if (res?.receipt) printOpReceipt(res.receipt, hospital, user);
      const data = await ipApi.getReceipts(80);
      const today = new Date().toISOString().slice(0, 10);
      setReceipts((data.receipts || []).filter((r) => (r.createdAt || '').slice(0, 10) === today));
      setMsg(`✓ Receipt saved & printed — Total: ${inrFmt(lineTotal)}`);
      setForm({ patientName: '', patientId: '', patientPhone: '', doctor: '', queueId: '', paymentMode: 'Cash', txnRef: '', notes: '', lines: [{ service: defaultService.label, desc: '', amount: defaultService.amount }] });
      if (onReceipt) onReceipt();
    } catch (err) { setMsg(err.message); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
        {[{ label: 'Today Bills', val: receipts.length, color: '#6366f1' }, { label: 'Today Total', val: inrFmt(receipts.reduce((s, r) => s + Number(r.amount || r.total || 0), 0)), color: '#10b981' }].map(s => (
          <div key={s.label} className="card" style={{ textAlign: 'center', padding: 14, borderTop: `3px solid ${s.color}` }}>
            <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 22, fontWeight: 900, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginTop: 3 }}>{s.label}</div>
          </div>
        ))}
      </div>
      {msg && <div className={`mb ${/fail|error|not|require/i.test(msg) ? 'error' : 'info-banner'}`} style={{ marginBottom: 12 }}>{msg}<button type="button" style={{ marginLeft: 10, fontWeight: 700, background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setMsg('')}>x</button></div>}
      <form className="card mb" onSubmit={createReceipt} style={{ borderLeft: '4px solid #10b981' }}>
        <h3 style={{ marginTop: 0, color: '#0d6e6a' }}>New Bill / Receipt</h3>
        <div style={{ marginBottom: 10 }}>
          <label style={{ fontSize: 13, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Quick pick from queue:</label>
          <select className="input" style={{ maxWidth: 340 }} onChange={e => pickFromQueue(e.target.value)}>
            <option value="">-- Select patient from today's queue --</option>
            {queue.filter((q) => q.status !== Q.COMPLETED).map((q) => (
              <option key={q.id} value={q.id}>{q.name || q.patientName} {q.token ? `#${q.token}` : ''} — {q.doctor || ''}</option>
            ))}
          </select>
        </div>
        <div className="form-grid mb">
          <div className="field"><label>Patient Name *</label><input className="input" required value={form.patientName} onChange={e => setForm(f => ({ ...f, patientName: e.target.value }))} placeholder="Full name" /></div>
          <div className="field"><label>UHID / Patient ID</label><input className="input" value={form.patientId} onChange={e => setForm(f => ({ ...f, patientId: e.target.value }))} placeholder="PT-XXXX" /></div>
          <div className="field"><label>Mobile</label><input className="input" value={form.patientPhone} onChange={e => setForm(f => ({ ...f, patientPhone: e.target.value }))} placeholder="10-digit" /></div>
          <div className="field"><label>Doctor</label><input className="input" value={form.doctor} onChange={e => setForm(f => ({ ...f, doctor: e.target.value }))} placeholder="Dr. Name" /></div>
          <div className="field"><label>Payment Mode</label>
            <select className="input" value={form.paymentMode} onChange={e => setForm(f => ({ ...f, paymentMode: e.target.value }))}>
              {PAYMENT_MODES.map(m => <option key={m}>{m}</option>)}
            </select>
          </div>
          <div className="field"><label>Txn Reference</label><input className="input" value={form.txnRef} onChange={e => setForm(f => ({ ...f, txnRef: e.target.value }))} placeholder="UPI/Card ref" /></div>
        </div>
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 6 }}>Quick Charge Presets:</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {PRESETS.map(p => (
              <button key={p.label} type="button" style={{ fontSize: 11, padding: '4px 10px', border: '1px solid #e2e8f0', borderRadius: 6, background: '#f8fafc', cursor: 'pointer', fontWeight: 600 }} onClick={() => setForm(f => ({ ...f, lines: [...f.lines, { service: p.label, desc: '', amount: p.amount }] }))}>+ {p.label} ({inrFmt(p.amount)})</button>
            ))}
          </div>
        </div>
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', marginBottom: 14 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#f1f5f9' }}><th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700 }}>Service</th><th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700 }}>Description</th><th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, width: 120 }}>Amount \u20b9</th><th style={{ width: 40 }}></th></tr></thead>
            <tbody>
              {form.lines.map((line, i) => (
                <tr key={i} style={{ borderTop: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 8px' }}><select className="input input-sm" value={line.service} onChange={e => updateLine(i, { service: e.target.value })} style={{ fontSize: 12 }}>{SERVICES.map(s => <option key={s}>{s}</option>)}</select></td>
                  <td style={{ padding: '6px 8px' }}><input className="input input-sm" value={line.desc} onChange={e => updateLine(i, { desc: e.target.value })} style={{ fontSize: 12 }} placeholder="Optional" /></td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}><input type="number" className="input input-sm" value={line.amount} onChange={e => updateLine(i, { amount: e.target.value })} style={{ fontSize: 12, textAlign: 'right', maxWidth: 90 }} /></td>
                  <td style={{ padding: '4px' }}>{form.lines.length > 1 && <button type="button" onClick={() => removeLine(i)} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 16 }}>x</button>}</td>
                </tr>
              ))}
            </tbody>
            <tfoot><tr style={{ background: '#f8fafc' }}><td colSpan={2} style={{ padding: '8px 10px', fontWeight: 700 }}>Total</td><td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'Outfit,sans-serif', fontWeight: 900, fontSize: 16, color: '#0d6e6a' }}>{inrFmt(lineTotal)}</td><td></td></tr></tfoot>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}>+ Add line</button>
          <div style={{ flex: 1 }}></div>
          <div style={{ fontFamily: 'Outfit,sans-serif', fontSize: 18, fontWeight: 900, color: '#0d6e6a' }}>{inrFmt(lineTotal)}</div>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving...' : '\u2713 Save & Print Receipt'}</button>
        </div>
      </form>
      {receipts.length > 0 && (
        <div className="card">
          <h3 style={{ marginTop: 0 }}>Today's Receipts (Session)</h3>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Time</th><th>Patient</th><th>Doctor</th><th>Mode</th><th>Total</th></tr></thead>
              <tbody>{receipts.map(r => (
                <tr key={r.id || r.receiptId}><td style={{ fontSize: 12 }}>{new Date(r.at || r.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</td><td><div style={{ fontWeight: 700 }}>{r.patientName}</div><div style={{ fontSize: 11, color: '#94a3b8' }}>{r.patientId}</div></td><td>{r.doctor || '\u2014'}</td><td><span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>{r.paymentMode}</span></td><td style={{ fontWeight: 700, color: '#10b981', fontFamily: 'Outfit,sans-serif' }}>{inrFmt(r.amount || r.total)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        </div>
      )}
      {receipts.length === 0 && <p className="hint table-empty">No receipts in this session. Use the form above to bill patients.</p>}
    </div>
  );
}
